import { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { Form, Input, Modal } from 'antd';

import {
  buildJurisdictionIdentifier,
  getAttendanceJurisdictionIdRules,
  isForeignPersonalCountry,
  type JurisdictionIdentifier,
} from '@src/packages/utils/nationalId';

type JurisdictionIdPromptModalProps = {
  open: boolean;
  organizationCountry?: string | null;
  /** User address / nationality country — drives strict vs loose identifier rules. */
  personalCountry?: string | null;
  personalIdCard?: string;
  userName?: string;
  onCancel: () => void;
  /** May be async (persist + continue). Modal shows loading until it settles. */
  onConfirm: (identifier: JurisdictionIdentifier) => void | Promise<void>;
};

/**
 * Shown only when a member's personal idCard cannot satisfy the organization
 * jurisdiction required for work journeys and attendance.
 */
const JurisdictionIdPromptModal = ({
  open,
  organizationCountry,
  personalCountry,
  personalIdCard,
  userName,
  onCancel,
  onConfirm,
}: JurisdictionIdPromptModalProps) => {
  const intl = useIntl();
  const [form] = Form.useForm<{ value: string }>();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const submittingRef = useRef(false);
  const rules = getAttendanceJurisdictionIdRules(organizationCountry, personalCountry);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setConfirmLoading(false);
      submittingRef.current = false;
    }
  }, [form, open]);

  const handleFinish = async ({ value }: { value: string }) => {
    if (submittingRef.current) {
      return;
    }
    const identifier = buildJurisdictionIdentifier(
      organizationCountry,
      value,
      personalCountry,
    );
    if (!identifier) {
      return;
    }
    submittingRef.current = true;
    setConfirmLoading(true);
    try {
      await onConfirm(identifier);
    } finally {
      submittingRef.current = false;
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    if (confirmLoading || submittingRef.current) {
      return;
    }
    onCancel();
  };

  if (!rules) {
    return null;
  }

  const isForeignWorker = isForeignPersonalCountry(organizationCountry, personalCountry);
  const descriptionId = isForeignWorker
    ? 'workJourney.jurisdictionId.description.foreign'
    : 'workJourney.jurisdictionId.description';

  return (
    <Modal
      open={open}
      title={intl.formatMessage(
        { id: 'workJourney.jurisdictionId.title' },
        { document: rules.documentKind },
      )}
      okText={intl.formatMessage({ id: 'button.continue' })}
      cancelText={intl.formatMessage({ id: 'button.cancel' })}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: confirmLoading }}
      cancelButtonProps={{ disabled: confirmLoading }}
      maskClosable={!confirmLoading}
      keyboard={!confirmLoading}
      closable={!confirmLoading}
      destroyOnClose
    >
      <p>
        {intl.formatMessage(
          { id: descriptionId },
          {
            document: rules.documentKind,
            personalId: personalIdCard || '—',
            user: userName || '—',
          },
        )}
      </p>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="value"
          label={intl.formatMessage({ id: rules.labelKey })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: 'form.required.msg' }),
            },
            {
              validator: async (_, value: string) => {
                if (!rules.validate(value || '')) {
                  throw new Error(intl.formatMessage({ id: rules.invalidKey }));
                }
              },
              message: intl.formatMessage({ id: rules.invalidKey }),
            },
          ]}
        >
          <Input
            placeholder={rules.placeholder}
            disabled={confirmLoading}
            onBlur={event => {
              form.setFieldValue('value', rules.format(event.currentTarget.value));
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default JurisdictionIdPromptModal;
